import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import ProjectContext from "@context/Project/projectContext";
import NiftiFileReader from "../NiftiFileReader";
import service from "@services/index";
import {
  fetchProjectLabels,
  labelsFromContextState,
  type ProjectLabel,
} from "../helpers";

const Navbar = styled.div`
  height: 75px;
  background: #f04a23;
  display: flex;
  align-items: center;
  padding: 0 24px;
  box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 
              0px 3px 4px 0px rgba(0,0,0,0.14), 
              0px 1px 8px 0px rgba(0,0,0,0.12);
`;

const NavbarTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 900;
  margin: 0;
  text-align: center;
  flex: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  height: calc(100vh - 75px); // Subtract navbar height
  width: 100%;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  border: 1px solid red;
`;

const Sidebar = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #f04a23;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: 15px;
  font-weight: 800;
  text-align: center;
`;

const ImageListContainer = styled.div`
  padding: 16px;
  height: calc(100vh - 145px); // Adjust for navbar (75px) + header height (70px)
  overflow-y: auto;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
`;

const ImageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageListItem = styled.li<{ isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#ffffff'};
  border: 1px solid ${props => props.isSelected ? '#f04a23' : '#e0e0e0'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${props => props.isSelected ? 'linear-gradient(to right, #f04a23, #ed8623a1)' : '#f5f5f5'};
    border-color: #f04a23;
  }

  /* File icon */
  &::before {
    content: '';
    display: block;
    width: 24px;
    height: 24px;
    background: ${props => props.isSelected ? 'white' : '#f04a23'};
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
    opacity: ${props => props.isSelected ? '1' : '0.8'};
  }
`;

const ImageName = styled.span<{ isSelected: boolean }>`
  font-size: 13px;
  color: ${props => props.isSelected ? 'white' : '#333'};
  font-weight: ${props => props.isSelected ? 'bold' : 'normal'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const NiftiViewerContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
`;

const ProgressContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  gap: 10px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 300px;
  width: 100%;
`;

const ProgressText = styled.div`
  color: #f04a23;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 20px;
  border: 1px solid #ed8623;
  border-radius: 10px;
  overflow: hidden;
  background-color: #fff;
`;

const ProgressBar = styled.div<{ progress: number }>`
  width: ${props => `${props.progress}%`};
  height: 100%;
  background: linear-gradient(to right, #f04a23, #ed8623);
  transition: width 0.3s ease-in-out;
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  text-align: center;
  color: #666;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled.h2`
  color: #f04a23;
  margin-bottom: 10px;
  font-size: 24px;
`;

const WelcomeText = styled.p`
  font-size: 16px;
  max-width: 500px;
  line-height: 1.6;
`;

function SegmentationDataLabellingPage() {
  const { project_type } = useParams<{ project_type: string }>();
  const projectContextInfo = useContext(ProjectContext);
  const { state: projectState } = projectContextInfo;
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [niftiFile, setNiftiFile] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  
  const [labels, setLabels] = useState<ProjectLabel[]>([]);
  const [preLoadedAnnotations, setPreLoadedAnnotations] = useState<any>(null);
  // Cache for storing downloaded NIFTI files
  const niftiCache = useRef<Map<string, ArrayBuffer>>(new Map());
  
  // Add the new getAnnotationData function
  const getAnnotationData = async (imageId: string) => {
    try {
      const response = await service.get(
        `/api/genie-3d/ct-segmentation/data/${projectState.projectId}/annotation/${imageId}`,
        null,
        null,
        'https://genie-be-3d-dev.avawatz.com'
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching annotation data:', error);
      return null;
    }
  };
  

  const fetchNiftiFile = useCallback(async (imageId: string) => {
    try {
      // Clear current NIFTI file while loading new one
      setNiftiFile(null);
      setLoading(true);
      setError(null);
      setDownloadProgress(0);

      // Check cache first
      if (niftiCache.current.has(imageId)) {
        console.log('Loading from cache:', imageId);
        setNiftiFile(niftiCache.current.get(imageId)!);
        setDownloadProgress(100);
        setLoading(false);
        return;
      }
      
      const response = await service.get(
        `/api/genie-3d/data/${projectState.projectId}/image/${imageId}`,
        null,
        null,
        'https://genie-be-3d-dev.avawatz.com',
        { 
          responseType: 'arraybuffer',
          onDownloadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentCompleted);
          }
        }
      );

      if (!response.data) {
        throw new Error('No data received from the server');
      }

      console.log('Array Buffer Size:', response.data.byteLength);
      
      // Store in cache
      niftiCache.current.set(imageId, response.data);
      
      setDownloadProgress(100);
      setNiftiFile(response.data);
      
      const annotationData = await getAnnotationData(imageId);
      console.log("annotationData ===>", annotationData);
      if(annotationData?.polygons) {
        setPreLoadedAnnotations(annotationData.polygons);
      }

    } catch (err) {
      console.error('Error fetching NIFTI file:', err);
      setError(err instanceof Error ? err.message : 'Failed to load NIFTI file');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectState.projectId]);

  useEffect(() => {
    if (!projectState?.projectId || projectState.projectId === -1) {
      return;
    }

    const fallbackLabels = labelsFromContextState(
      projectState.labels,
      projectState.colors
    );

    fetchProjectLabels(projectState.projectId, project_type, fallbackLabels).then(
      (loadedLabels) => {
        setLabels(loadedLabels);
      }
    );
  }, [projectState.projectId, projectState.labels, projectState.colors, project_type]);

  useEffect(() => {
    const cache = niftiCache.current;
    return () => {
      cache.clear();
    };
  }, []);

  // Optional: Limit cache size
  const limitCacheSize = useCallback((maxSize: number = 5) => {
    if (niftiCache.current.size > maxSize) {
      const firstKey: any = niftiCache.current.keys().next().value;
      niftiCache.current.delete(firstKey);
    }
  }, []);

  useEffect(() => {
    if (selectedImageId) {
      fetchNiftiFile(selectedImageId);
      limitCacheSize();
    }
  }, [selectedImageId, fetchNiftiFile, limitCacheSize]);

  return (
    <PageContainer className="page-container123">
      <Navbar>
        <NavbarTitle>3D CT Segmentation Data Labelling</NavbarTitle>
      </Navbar>
      <ContentWrapper>
        <Sidebar>
          <SidebarHeader>
            <SidebarTitle>Image List</SidebarTitle>
          </SidebarHeader>
          <ImageListContainer>
            <ImageList>
              {projectState?.imageIds?.map((imageId: string) => (
                <ImageListItem
                  key={imageId}
                  isSelected={selectedImageId === imageId}
                  onClick={() => setSelectedImageId(imageId)}
                >
                  <ImageName isSelected={selectedImageId === imageId}>
                    {imageId}
                  </ImageName>
                </ImageListItem>
              ))}
            </ImageList>
          </ImageListContainer>
        </Sidebar>
        <MainContent>
          {loading && (
            <ProgressContainer>
              <ProgressWrapper>
                <ProgressText>Loading NIFTI file... {downloadProgress}%</ProgressText>
                <ProgressBarContainer>
                  <ProgressBar progress={downloadProgress} />
                </ProgressBarContainer>
              </ProgressWrapper>
            </ProgressContainer>
          )}
          {error && (
            <div style={{ 
              color: 'red',
              padding: '20px',
              textAlign: 'center' 
            }}>
              {error}
            </div>
          )}
          {!loading && !niftiFile && !error && (
            <WelcomeContainer style={{ width: '100%' }}>
              <WelcomeIcon />
              <WelcomeTitle>Welcome to NIFTI Viewer</WelcomeTitle>
              <WelcomeText>
                Select an image from the list on the left to start viewing and analyzing NIFTI files. 
                The viewer supports various visualization options and tools for detailed analysis.
              </WelcomeText>
            </WelcomeContainer>
          )}
          {!loading && niftiFile && (
            <NiftiViewerContainer>
              <NiftiFileReader initialNiftiData={niftiFile} projectId={projectState.projectId} imageId={selectedImageId ?? ''} labelsFromContext={labels} preLoadedAnnotations={preLoadedAnnotations} />
            </NiftiViewerContainer>
          )}
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
}

export default SegmentationDataLabellingPage;
